import { useCallback, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { PATH_MAP } from "../../configuration/PathMap"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import type { SignUp } from "../../Objects/SignUp/SignUp"
import type { SignInResponseDto } from "../../Objects/Generics/SignInResponseDto"
import { PostFormRequest } from "../../services/ApiClientService"
import { trackCompleteRegistrationConversion, trackMarketingEvent } from "../../analytics/tracking"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { employeeMemory } from "../../Objects/Memory/Memory"
import { useGetEmployee } from "../Employee/useGetEmployee"

type GoogleCredentialResponse = {
  credential?: string
}

type GoogleAccountsId = {
  initialize: (options: { client_id: string; callback: (response: GoogleCredentialResponse) => void }) => void
  renderButton: (
    parent: HTMLElement,
    options: {
      theme: "outline" | "filled_blue" | "filled_black"
      size: "large" | "medium" | "small"
      type: "standard" | "icon"
      shape: "rectangular" | "pill" | "circle" | "square"
      text: "signup_with" | "signin_with" | "continue_with" | "signin"
      width: number
      locale: string
    },
  ) => void
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GoogleAccountsId
      }
    }
  }
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim()

const googleScriptId = "google-identity-services"

interface UseGoogleSignUpOptions {
  signUp: SignUp
  canSubmit: () => boolean
}

export function useGoogleSignUp({ signUp, canSubmit }: UseGoogleSignUpOptions) {
  const navigate = useNavigate()

  const { t } = useTranslation("general")

  const { t: tSignup } = useTranslation("signup")

  const { setError } = useError()

  const { enableLoad, disableLoad } = useLoad()

  const { setEmployee } = useEmployerLogged()

  const { handleGetEmployee } = useGetEmployee()

  const googleButtonRef = useRef<HTMLDivElement | null>(null)

  const latestSignUpRef = useRef(signUp)

  const latestCanSubmitRef = useRef(canSubmit)

  const renderedRef = useRef(false)

  latestSignUpRef.current = signUp

  latestCanSubmitRef.current = canSubmit

  const handleGoogleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      if (!latestCanSubmitRef.current()) {
        return
      }

      if (!response.credential) {
        setError(t("label_title_erro"), tSignup("google_token_error"))
        return
      }

      enableLoad()

      try {
        const currentSignUp = latestSignUpRef.current
        const result = await PostFormRequest<SignInResponseDto>(API_ROUTES.ACCOUNT.CREATE_WITH_GOOGLE, {
          ...currentSignUp.toJSON(),
          isAgreedWithTerms: true,
          googleIdToken: response.credential,
        })

        if (result.success && result.data?.token) {
          localStorage.setItem(API_ROUTES.TOKEN, JSON.stringify(result.data.token))
          localStorage.setItem(API_ROUTES.EMAIL_KEY, JSON.stringify(result.data.username))

          await handleGetEmployee(() => {})

          setEmployee(employeeMemory.findById(result.data.id) ?? null)

          trackMarketingEvent("landing_signup_success", {
            source: "store_google_signup",
            plan: currentSignUp.eaccountPlansType,
          })

          trackCompleteRegistrationConversion({
            content_name: "store_account_google",
            status: "created",
            plan: currentSignUp.eaccountPlansType,
          })

          navigate(PATH_MAP.COMMANDS_PAGE)
          return
        }

        throw new Error(result.message)
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : t("label_unknown_error_description")
        setError(t("label_title_erro"), message)
      } finally {
        disableLoad()
      }
    },
    [disableLoad, enableLoad, handleGetEmployee, navigate, setEmployee, setError, t, tSignup],
  )

  useEffect(() => {
    if (!googleClientId || renderedRef.current) {
      return
    }

    const renderGoogleButton = () => {
      if (!googleButtonRef.current || !window.google?.accounts?.id || renderedRef.current) {
        return
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => void handleGoogleCredential(response),
      })

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        type: "standard",
        shape: "rectangular",
        text: "continue_with",
        width: Math.min(400, Math.max(260, Math.floor(googleButtonRef.current.getBoundingClientRect().width))),
        locale: "pt-BR",
      })

      renderedRef.current = true
    }

    const existingScript = document.getElementById(googleScriptId)

    if (existingScript) {
      renderGoogleButton()
      return
    }

    const script = document.createElement("script")
    script.id = googleScriptId
    script.src = "https://accounts.google.com/gsi/client"
    script.async = true
    script.defer = true
    script.onload = renderGoogleButton
    script.onerror = () => setError(t("label_title_erro"), tSignup("google_script_error"))

    document.head.appendChild(script)
  }, [handleGoogleCredential, setError, t, tSignup])

  return {
    googleButtonRef,
    isGoogleSignUpAvailable: Boolean(googleClientId),
  }
}
